// TASK-20260825-twap-fee-headroom-and-rejection-visibility (A1) — making a failing slice legible.
//
// The engine treats a child rejection as NON-FATAL and keeps cycling, which is correct: a slice that
// cannot fund now may fund later. But the blotter rendered a rejected child the same muted grey as a
// cancelled one and never showed its reason, so a slice that was rejected INSUFFICIENT_FUND and
// re-placed every 60 s until the deadline looked like it was simply "In Queue". Seven silent retries
// on a live parent read as a hang.
//
// Reasons arrive already sanitized (A2): a stable code — INSUFFICIENT_FUNDS / BELOW_MIN_SIZE /
// POST_ONLY / UNKNOWN_REJECTION — optionally followed by ": <scrubbed residual>". Never raw venue text.

/** Reason codes the server may send, mapped to something a desk can read at a glance. */
const REASON_LABELS = {
  INSUFFICIENT_FUNDS: 'Insufficient funds',
  BELOW_MIN_SIZE: 'Below venue minimum',
  POST_ONLY: 'Post-only would cross',
  UNKNOWN_REJECTION: 'Rejected by venue',
};

/** True when a child's status is a venue rejection (as opposed to a cancel/expiry, which is normal). */
export function isRejected(status) {
  return /^reject/i.test(String(status ?? ''));
}

/**
 * A human label for a sanitized reason string. Keeps any scrubbed residual after the code, so an
 * unrecognized rejection still carries its (safe) detail.
 */
export function reasonLabel(notes) {
  const raw = String(notes ?? '').trim();
  if (!raw) return null;

  const [code, ...rest] = raw.split(':');
  const known = REASON_LABELS[code.trim()];
  if (!known) return raw;

  const residual = rest.join(':').trim();
  return residual ? `${known} — ${residual}` : known;
}

/**
 * Collapses the blotter's rows into per-cycle attempts. The engine re-places a failing slice under the
 * SAME CycleIndex every interval, so N rejections of one cycle are N attempts at one slice — not N
 * unrelated slices. Returns one entry per cycle, newest attempt last, with the attempt count and the
 * reason of the most recent failure.
 */
export function cycleAttempts(children) {
  const byCycle = new Map();
  for (const c of Array.isArray(children) ? children : []) {
    const key = c?.cycleIndex ?? -1;
    if (!byCycle.has(key)) byCycle.set(key, []);
    byCycle.get(key).push(c);
  }

  return [...byCycle.entries()]
    .map(([cycleIndex, attempts]) => {
      const rejections = attempts.filter((a) => isRejected(a.status));
      const last = rejections[rejections.length - 1] ?? null;
      return {
        cycleIndex,
        attempts: attempts.length,
        rejectedAttempts: rejections.length,
        // A cycle is BLOCKED when every attempt so far was rejected — it has never partially worked.
        blocked: attempts.length > 0 && rejections.length === attempts.length,
        lastReason: last ? reasonLabel(last.notes) : null,
      };
    })
    .sort((a, b) => a.cycleIndex - b.cycleIndex);
}

/**
 * The parent-level warning (AC 9): a cycle that has failed twice or more in a row is not "slow", it is
 * stuck, and the desk should see that without opening the blotter. Returns null when nothing is wrong.
 */
export function blockedCycleWarning(children) {
  const blocked = cycleAttempts(children).filter((c) => c.blocked && c.rejectedAttempts >= 2);
  if (blocked.length === 0) return null;

  const worst = blocked.reduce((a, b) => (b.rejectedAttempts > a.rejectedAttempts ? b : a));
  return {
    cycleIndex: worst.cycleIndex,
    attempts: worst.rejectedAttempts,
    reason: worst.lastReason,
    text: `Cycle ${worst.cycleIndex} rejected ${worst.rejectedAttempts}×${worst.lastReason ? ` — ${worst.lastReason}` : ''}`,
  };
}
